/**
 * ReadingWizardModal - Step-by-step wizard for "Get My Free Reading"
 *
 * Flow:
 *   Step 0: "When were you born?" — Date picker
 *   Step 1: "Do you know your exact birth time?" — Exact / Approx / Unknown
 *   Step 2: "Where were you born?" — City autocomplete
 *   Step 3: Loading → Reading result
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box, Button, TextField, Typography, Collapse, IconButton, Modal,
  CircularProgress, Card, List, ListItemButton, ListItemText,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { motion, AnimatePresence } from 'framer-motion';
import { cosmicAPI } from '../services/cosmicConciergeService';
import { analytics } from '../context/AnalyticsContext-v2';
import { lookupCity, geocodeCity, getUTCOffset } from '../utils/location';
import { calculatePlanetaryPositions, calculateAscendant, calculateHouses } from '../utils/ephemeris';

// -- Theme (matches BirthDataWizard + site) --
const COLORS = {
  background: '#0a0a0f',
  surface: '#12121a',
  accent: '#c9a87c',
  accentDark: '#a88a5e',
  text: '#ede8e0',
  textMuted: '#8a8a9a',
  border: 'rgba(201, 168, 124, 0.2)',
};

const INPUT_STEPS = 3; // date, time, city

const EXPLANATIONS = {
  0: 'Your birth date determines your Sun sign, Moon sign, and the positions of all planets at the moment you were born. This is the foundation of your entire natal chart.',
  1: 'Your birth time determines your Rising sign (Ascendant) and the house placements of your planets. Even a 15-minute difference can shift your Rising sign. If you don\'t know your exact time, we can still create a meaningful reading.',
  2: 'Your birth location determines which part of the sky was visible at your birth. Combined with your birth time, this pinpoints your Rising sign and the exact degree of every house cusp in your chart.',
};

const TIME_OPTIONS = [
  { value: 'exact', label: 'Yes, I know it', description: 'From birth certificate or parent' },
  { value: 'approximate', label: 'Approximately', description: 'Within an hour or so' },
  { value: 'unknown', label: "I don't know", description: "We'll use a noon chart" },
];

// -- Progress dots --
function ProgressDots({ current, total }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
      {Array.from({ length: total }, (_, i) => (
        <Box
          key={i}
          sx={{
            width: i === current ? 24 : 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: i <= current ? COLORS.accent : COLORS.border,
            transition: 'all 0.3s ease',
          }}
        />
      ))}
    </Box>
  );
}

// -- Step animation variants --
const stepVariants = {
  enter: (direction) => ({ x: direction > 0 ? 200 : -200, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 300, damping: 30 } },
  exit: (direction) => ({ x: direction > 0 ? -200 : 200, opacity: 0, transition: { duration: 0.2 } }),
};

export default function ReadingWizardModal({ open, onClose, onSeePlans }) {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);

  // Form state
  const [birthDate, setBirthDate] = useState(null);
  const [timeConfidence, setTimeConfidence] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthCity, setBirthCity] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [timezone, setTimezone] = useState(null);

  // UI state
  const [showExplanation, setShowExplanation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [readingResult, setReadingResult] = useState(null);

  // City search / geocoding state
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [citySearching, setCitySearching] = useState(false);
  const [citySelected, setCitySelected] = useState(false);
  const debounceRef = useRef(null);

  const goNext = useCallback(() => {
    setDirection(1);
    setShowExplanation(false);
    setStep((s) => s + 1);
  }, []);

  const goBack = useCallback(() => {
    setDirection(-1);
    setShowExplanation(false);
    setStep((s) => Math.max(s - 1, 0));
  }, []);

  const handleCityInput = (value) => {
    setBirthCity(value);
    setCitySelected(false);
    setLatitude(null);
    setLongitude(null);
    setTimezone(null);

    // First try static database for instant match
    const staticMatch = lookupCity(value);
    if (staticMatch) {
      setLatitude(staticMatch.latitude);
      setLongitude(staticMatch.longitude);
      setTimezone(staticMatch.timezone || null);
      setCitySelected(true);
      setCitySuggestions([]);
      return;
    }

    // Debounced geocoding API call
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 3) {
      setCitySuggestions([]);
      return;
    }

    setCitySearching(true);
    debounceRef.current = setTimeout(async () => {
      const results = await geocodeCity(value);
      setCitySuggestions(results);
      setCitySearching(false);
    }, 400);
  };

  const handleCitySelect = (suggestion) => {
    setBirthCity(suggestion.cityName);
    setLatitude(suggestion.latitude);
    setLongitude(suggestion.longitude);
    setTimezone(suggestion.timezone || null);
    setCitySelected(true);
    setCitySuggestions([]);
  };

  // Clean up debounce on unmount
  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const canProceed = () => {
    switch (step) {
      case 0: return !!birthDate;
      case 1: return !!timeConfidence && (timeConfidence === 'unknown' || !!birthTime);
      case 2: return citySelected && latitude != null;
      default: return false;
    }
  };

  /**
   * Submit birth data → call API → show reading
   */
  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError('');
    analytics.wizardStart();

    try {
      const today = new Date().toISOString().split('T')[0];
      const timeStr = timeConfidence === 'unknown' ? '12:00' : birthTime || '12:00';
      const [h, m] = timeStr.split(':').map(Number);

      // Build natal date using the same method as FreeBirthChartPage/SimpleWheelFixed
      const year = birthDate.getFullYear();
      const month = birthDate.getMonth();
      const day = birthDate.getDate();

      // Calculate UTC offset the same way FreeBirthChartPage does
      const localNatalDate = new Date(year, month, day, h, m, 0);
      const utcOffset = timezone ? getUTCOffset(localNatalDate, timezone) : 0;

      // Convert local birth time to true UTC — identical to SimpleWheelFixed line 74
      const natalDateUTC = new Date(
        Date.UTC(year, month, day, h, m, 0) - utcOffset * 60 * 60 * 1000
      );

      const natalLat = latitude || 53.35;
      const natalLon = longitude || -6.26;

      // Calculate natal chart client-side (same functions as FreeBirthChartPage)
      const natalPlanets = calculatePlanetaryPositions(natalDateUTC);
      const ascendant = calculateAscendant(natalDateUTC, natalLat, natalLon);
      const natalHouseCusps = calculateHouses(natalDateUTC, natalLat, natalLon);

      // Calculate today's transits client-side too
      const todayDate = new Date();
      const transitPlanets = calculatePlanetaryPositions(todayDate);

      const SIGNS = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'];
      const HOUSE_NAMES = ['1st','2nd','3rd','4th','5th','6th','7th','8th','9th','10th','11th','12th'];
      const HOUSE_THEMES = [
        'Identity & Self',
        'Money & Values',
        'Communication & Learning',
        'Home & Family',
        'Creativity & Romance',
        'Work & Health',
        'Partnerships & Marriage',
        'Shared Resources & Transformation',
        'Higher Learning & Travel',
        'Career & Public Life',
        'Friends & Future Goals',
        'Spirituality & Subconscious',
      ];

      // Determine which natal house a planet falls in (whole sign)
      const ascSignIdx = Math.floor(ascendant / 30);
      const getHouse = (longitude) => {
        const signIdx = Math.floor(((longitude % 360) + 360) % 360 / 30);
        return ((signIdx - ascSignIdx + 12) % 12);
      };

      // Format natal positions with house placements
      const natalPositions = {};
      for (const planet of natalPlanets) {
        const houseIdx = getHouse(planet.longitude);
        natalPositions[planet.name] = {
          longitude: planet.longitude,
          sign: planet.sign,
          degree: planet.degree,
          retrograde: planet.retrograde || false,
          house: houseIdx + 1,
          houseName: HOUSE_NAMES[houseIdx],
          houseTheme: HOUSE_THEMES[houseIdx],
        };
      }
      natalPositions['Ascendant'] = {
        longitude: ascendant,
        sign: SIGNS[Math.floor(ascendant / 30)],
        degree: ascendant % 30,
        house: 1,
        houseName: '1st',
        houseTheme: HOUSE_THEMES[0],
      };

      // Format transit positions with house placements (in the natal house system)
      const positions = {};
      for (const planet of transitPlanets) {
        const houseIdx = getHouse(planet.longitude);
        positions[planet.name] = {
          longitude: planet.longitude,
          sign: planet.sign,
          degree: planet.degree,
          retrograde: planet.retrograde || false,
          transitHouse: houseIdx + 1,
          transitHouseName: HOUSE_NAMES[houseIdx],
          transitHouseTheme: HOUSE_THEMES[houseIdx],
        };
      }

      // Build house summary for the AI reading
      const houseSummary = natalHouseCusps.map((cusp, i) => ({
        house: i + 1,
        name: HOUSE_NAMES[i],
        theme: HOUSE_THEMES[i],
        sign: SIGNS[Math.floor(cusp / 30)],
      }));

      // Calculate aspects between two sets of positions
      const ASPECT_DEFS = [
        { name: 'Conjunction', angle: 0, orb: 8 },
        { name: 'Opposition', angle: 180, orb: 8 },
        { name: 'Trine', angle: 120, orb: 6 },
        { name: 'Square', angle: 90, orb: 6 },
        { name: 'Sextile', angle: 60, orb: 4 },
      ];

      const findAspects = (pos1, pos2, isCross) => {
        const aspects = [];
        const keys1 = Object.keys(pos1).filter(k => k !== 'Ascendant');
        const keys2 = Object.keys(pos2).filter(k => k !== 'Ascendant');
        for (const p1 of keys1) {
          for (const p2 of keys2) {
            if (!isCross && keys1.indexOf(p1) >= keys2.indexOf(p2)) continue;
            const lon1 = pos1[p1].longitude;
            const lon2 = pos2[p2].longitude;
            let diff = Math.abs(lon1 - lon2);
            if (diff > 180) diff = 360 - diff;
            for (const asp of ASPECT_DEFS) {
              const orb = Math.abs(diff - asp.angle);
              if (orb <= asp.orb) {
                aspects.push({
                  planet1: isCross ? `Transit ${p1}` : p1,
                  planet2: isCross ? `Natal ${p2}` : p2,
                  transitPlanet: isCross ? p1 : undefined,
                  natalPlanet: isCross ? p2 : undefined,
                  aspectType: asp.name,
                  orb: parseFloat(orb.toFixed(2)),
                  applying: lon1 < lon2,
                  // Add house context for cross-aspects
                  ...(isCross && pos1[p1].transitHouse ? {
                    transitHouse: pos1[p1].transitHouseName,
                    transitHouseTheme: pos1[p1].transitHouseTheme,
                  } : {}),
                  ...(isCross && pos2[p2].house ? {
                    natalHouse: pos2[p2].houseName,
                    natalHouseTheme: pos2[p2].houseTheme,
                  } : {}),
                });
                break;
              }
            }
          }
        }
        // Sort by orb (tightest first)
        return aspects.sort((a, b) => a.orb - b.orb);
      };

      // Transit-to-transit aspects (what's happening in the sky today)
      const transitAspects = findAspects(positions, positions, false);

      // Natal-to-transit aspects (how today's sky is hitting YOUR chart)
      const natalToTransitAspects = findAspects(positions, natalPositions, true);

      const transits = {
        date: todayDate.toISOString(),
        positions,
        natalPositions,
        houseSummary,
        aspects: transitAspects,
        natalToTransitAspects,
      };

      const birthData = {
        date: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        time: timeStr,
        latitude: natalLat,
        longitude: natalLon,
        utcOffset,
      };

      const reading = await cosmicAPI.generateReading(today, transits, birthData, 'whatsapp');

      setReadingResult({
        ...transits,
        summary: reading?.briefing?.whatsapp || null,
      });
      analytics.previewRevealed();

      // Move to result step
      setDirection(1);
      setStep(3);
    } catch (err) {
      console.error('Reading generation failed:', err);
      setError('Could not generate your reading right now. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [birthDate, birthTime, timeConfidence, latitude, longitude, timezone]);

  const handleClose = () => {
    // Reset state for next open
    setStep(0);
    setDirection(1);
    setBirthDate(null);
    setTimeConfidence('');
    setBirthTime('');
    setBirthCity('');
    setLatitude(null);
    setLongitude(null);
    setTimezone(null);
    setShowExplanation(false);
    setLoading(false);
    setError('');
    setReadingResult(null);
    setCitySuggestions([]);
    setCitySearching(false);
    setCitySelected(false);
    onClose();
  };

  const isResultStep = step === 3;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: COLORS.background,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          pt: 'env(safe-area-inset-top, 0px)',
          pb: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 2, pb: 1 }}>
          {step > 0 && !isResultStep ? (
            <IconButton onClick={goBack} sx={{ color: COLORS.textMuted }}>
              <ArrowBackIcon />
            </IconButton>
          ) : (
            <Box sx={{ width: 40 }} />
          )}
          <Box sx={{ flex: 1 }}>
            {!isResultStep && <ProgressDots current={step} total={INPUT_STEPS} />}
          </Box>
          <IconButton onClick={handleClose} sx={{ color: COLORS.textMuted }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Step content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: isResultStep ? 'flex-start' : 'center',
            px: { xs: 2.5, sm: 3 },
            py: 2,
            maxWidth: 480,
            mx: 'auto',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              style={{ width: '100%' }}
            >
              {/* ===== STEP 0: Birth Date ===== */}
              {step === 0 && (
                <Box>
                  <Typography sx={{ color: COLORS.text, fontSize: '1.8rem', fontWeight: 600, mb: 1 }}>
                    When were you born?
                  </Typography>
                  <Typography sx={{ color: COLORS.textMuted, mb: 3, fontSize: '0.95rem' }}>
                    Select your date of birth
                  </Typography>
                  <Box
                    sx={{
                      '& .react-datepicker-wrapper': { width: '100%' },
                      '& .react-datepicker__input-container input': {
                        width: '100%',
                        padding: '16px',
                        fontSize: '1.1rem',
                        backgroundColor: COLORS.surface,
                        color: COLORS.text,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '12px',
                        outline: 'none',
                        textAlign: 'center',
                        letterSpacing: '0.05em',
                        boxSizing: 'border-box',
                      },
                      '& .react-datepicker__input-container input:focus': {
                        borderColor: COLORS.accent,
                      },
                    }}
                  >
                    <DatePicker
                      selected={birthDate}
                      onChange={(date) => setBirthDate(date)}
                      dateFormat="dd MMMM yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                      minDate={new Date('1920-01-01')}
                      placeholderText="Select your birth date"
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                    />
                  </Box>
                </Box>
              )}

              {/* ===== STEP 1: Birth Time ===== */}
              {step === 1 && (
                <Box>
                  <Typography sx={{ color: COLORS.text, fontSize: '1.8rem', fontWeight: 600, mb: 1 }}>
                    Do you know your exact birth time?
                  </Typography>
                  <Typography sx={{ color: COLORS.textMuted, mb: 3, fontSize: '0.95rem' }}>
                    This helps us calculate your Rising sign
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    {TIME_OPTIONS.map((opt) => (
                      <Button
                        key={opt.value}
                        variant="outlined"
                        onClick={() => {
                          setTimeConfidence(opt.value);
                          if (opt.value === 'unknown') setBirthTime('');
                        }}
                        sx={{
                          py: 2, px: 3,
                          justifyContent: 'flex-start', textAlign: 'left',
                          borderRadius: '12px', textTransform: 'none',
                          borderColor: timeConfidence === opt.value ? COLORS.accent : COLORS.border,
                          backgroundColor: timeConfidence === opt.value ? 'rgba(201,168,124,0.08)' : 'transparent',
                          color: COLORS.text,
                          '&:hover': { borderColor: COLORS.accent, backgroundColor: 'rgba(201,168,124,0.05)' },
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>{opt.label}</Typography>
                          <Typography sx={{ color: COLORS.textMuted, fontSize: '0.8rem' }}>{opt.description}</Typography>
                        </Box>
                      </Button>
                    ))}
                  </Box>

                  {(timeConfidence === 'exact' || timeConfidence === 'approximate') && (
                    <Box>
                      <TextField
                        type="time"
                        value={birthTime}
                        onChange={(e) => setBirthTime(e.target.value)}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: COLORS.text,
                            backgroundColor: COLORS.surface,
                            borderRadius: '12px',
                            fontSize: '1.2rem',
                            '& fieldset': { borderColor: COLORS.border },
                            '&:hover fieldset': { borderColor: COLORS.accent },
                            '&.Mui-focused fieldset': { borderColor: COLORS.accent },
                          },
                          '& input': { textAlign: 'center' },
                        }}
                      />
                      {timeConfidence === 'approximate' && (
                        <Typography sx={{ color: COLORS.textMuted, fontSize: '0.8rem', mt: 1, textAlign: 'center' }}>
                          Enter your best estimate — even an approximate time helps
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* ===== STEP 2: Birth City ===== */}
              {step === 2 && (
                <Box>
                  <Typography sx={{ color: COLORS.text, fontSize: '1.8rem', fontWeight: 600, mb: 1 }}>
                    Where were you born?
                  </Typography>
                  <Typography sx={{ color: COLORS.textMuted, mb: 3, fontSize: '0.95rem' }}>
                    Start typing your city or town
                  </Typography>

                  <TextField
                    value={birthCity}
                    onChange={(e) => handleCityInput(e.target.value)}
                    placeholder="e.g. Dublin, Ireland"
                    fullWidth
                    autoFocus
                    autoComplete="off"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: COLORS.text,
                        backgroundColor: COLORS.surface,
                        borderRadius: '12px',
                        fontSize: '1.1rem',
                        '& fieldset': { borderColor: citySelected ? COLORS.accent : COLORS.border },
                        '&:hover fieldset': { borderColor: COLORS.accent },
                        '&.Mui-focused fieldset': { borderColor: COLORS.accent },
                      },
                      '& .MuiOutlinedInput-input::placeholder': { color: COLORS.textMuted, opacity: 1 },
                    }}
                  />

                  {/* Search status */}
                  {citySearching && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={14} sx={{ color: COLORS.textMuted }} />
                      <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                        Searching...
                      </Typography>
                    </Box>
                  )}

                  {/* Geocoded suggestions dropdown */}
                  {citySuggestions.length > 0 && !citySelected && (
                    <List
                      sx={{
                        mt: 1,
                        borderRadius: '12px',
                        backgroundColor: COLORS.surface,
                        border: `1px solid ${COLORS.border}`,
                        overflow: 'hidden',
                        p: 0,
                      }}
                    >
                      {citySuggestions.map((s, i) => (
                        <ListItemButton
                          key={i}
                          onClick={() => handleCitySelect(s)}
                          sx={{
                            py: 1.5, px: 2,
                            '&:hover': { backgroundColor: 'rgba(201,168,124,0.08)' },
                            borderBottom: i < citySuggestions.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                          }}
                        >
                          <ListItemText
                            primary={s.cityName}
                            primaryTypographyProps={{ sx: { color: COLORS.text, fontSize: '0.95rem' } }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}

                  {/* No results message */}
                  {birthCity.trim().length >= 3 && !citySelected && !citySearching && citySuggestions.length === 0 && (
                    <Typography variant="caption" sx={{ color: COLORS.textMuted, mt: 1, display: 'block' }}>
                      No results found. Try a different spelling or a nearby larger city.
                    </Typography>
                  )}

                  {/* Confirmed selection */}
                  {citySelected && latitude && (
                    <Typography variant="caption" sx={{ color: COLORS.accent, mt: 1, display: 'block' }}>
                      Location found ({latitude.toFixed(2)}°N, {longitude.toFixed(2)}°E)
                    </Typography>
                  )}
                </Box>
              )}

              {/* ===== STEP 3: Loading / Result ===== */}
              {step === 3 && (
                <Box sx={{ textAlign: 'center' }}>
                  {loading ? (
                    <Box sx={{ py: 8 }}>
                      <CircularProgress sx={{ color: COLORS.accent, mb: 3 }} size={48} />
                      <Typography sx={{ color: COLORS.text, fontSize: '1.2rem', fontWeight: 600 }}>
                        Reading your stars...
                      </Typography>
                      <Typography sx={{ color: COLORS.textMuted, mt: 1, fontSize: '0.9rem' }}>
                        Calculating your unique planetary alignments
                      </Typography>
                    </Box>
                  ) : error ? (
                    <Box sx={{ py: 6 }}>
                      <Typography sx={{ color: '#ef4444', mb: 2 }}>{error}</Typography>
                      <Button
                        variant="outlined"
                        onClick={() => { setStep(2); setDirection(-1); }}
                        sx={{ color: COLORS.accent, borderColor: COLORS.accent, textTransform: 'none' }}
                      >
                        Go back and try again
                      </Button>
                    </Box>
                  ) : readingResult ? (
                    <Box sx={{ textAlign: 'left' }}>
                      <Card
                        sx={{
                          p: 3,
                          background: 'linear-gradient(135deg, rgba(201,168,124,0.08), rgba(201,168,124,0.02))',
                          border: '1px solid rgba(201,168,124,0.2)',
                          borderRadius: 3,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <AutoAwesomeIcon sx={{ color: COLORS.accent }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.accent }}>
                            Your Cosmic Snapshot
                          </Typography>
                        </Box>
                        <Typography
                          variant="body1"
                          component="div"
                          sx={{ color: COLORS.text, mb: 3, lineHeight: 1.7, whiteSpace: 'pre-line' }}
                        >
                          {readingResult.summary || 'The stars have a message for you.'}
                        </Typography>
                        <Button
                          variant="contained"
                          fullWidth
                          endIcon={<ArrowForwardIcon />}
                          onClick={() => { handleClose(); if (onSeePlans) onSeePlans(); }}
                          sx={{
                            background: 'linear-gradient(135deg, #c9a87c, #a88a5e)',
                            color: '#0a0a0f',
                            fontWeight: 600,
                            py: 1.5,
                            borderRadius: '12px',
                            fontSize: '1rem',
                            textTransform: 'none',
                            '&:hover': { background: 'linear-gradient(135deg, #dbc4a0, #c9a87c)' },
                          }}
                        >
                          Unlock Your Full Reading
                        </Button>
                      </Card>
                      <Typography
                        variant="body2"
                        onClick={handleClose}
                        sx={{
                          mt: 2, textAlign: 'center',
                          color: COLORS.textMuted,
                          cursor: 'pointer',
                          '&:hover': { color: COLORS.accent },
                        }}
                      >
                        Close
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Why do we need this? (input steps only) */}
          {!isResultStep && (
            <Box sx={{ mt: 3 }}>
              <Button
                size="small"
                startIcon={<HelpOutlineIcon />}
                onClick={() => setShowExplanation(!showExplanation)}
                sx={{
                  color: COLORS.textMuted,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  '&:hover': { color: COLORS.accent },
                }}
              >
                Why do we need this?
              </Button>
              <Collapse in={showExplanation}>
                <Box
                  sx={{
                    mt: 1, p: 2,
                    borderRadius: '12px',
                    backgroundColor: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                  }}
                >
                  <Typography sx={{ color: COLORS.textMuted, fontSize: '0.85rem', lineHeight: 1.6 }}>
                    {EXPLANATIONS[step]}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>

        {/* Bottom navigation button (input steps only) */}
        {!isResultStep && (
          <Box sx={{ px: { xs: 2.5, sm: 3 }, pb: 3, maxWidth: 480, mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
            <Button
              variant="contained"
              fullWidth
              disabled={!canProceed() || loading}
              onClick={step === INPUT_STEPS - 1 ? () => { goNext(); handleSubmit(); } : goNext}
              endIcon={
                loading ? <CircularProgress size={20} sx={{ color: '#0a0a0f' }} /> :
                step === INPUT_STEPS - 1 ? <AutoAwesomeIcon /> : <ArrowForwardIcon />
              }
              sx={{
                py: 1.5,
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: COLORS.accent,
                color: COLORS.background,
                '&:hover': { backgroundColor: COLORS.accentDark },
                '&.Mui-disabled': { backgroundColor: COLORS.surface, color: COLORS.textMuted },
                mb: 'env(safe-area-inset-bottom, 0px)',
              }}
            >
              {loading ? 'Reading your stars...' : step === INPUT_STEPS - 1 ? 'Get My Free Reading' : 'Continue'}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
